import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { checkEmailAvailability } from 'redux/thunks/check-email-availability'
import { consultationApplyCoupon } from 'redux/thunks/consultation-apply-coupon'
import { consultationCheckout } from 'redux/thunks/consultation-checkout'
import { createConsultation } from 'redux/thunks/create-consultation'
import { generatePixCode } from 'redux/thunks/generate-pix-code'
import { getDoctors } from 'redux/thunks/get-doctors'
import { getSurvey } from 'redux/thunks/get-survey'
import { PatientAttributes, PaymentResponse, PaymentSource } from 'redux/types'
import railsApiService from 'services/api/rails-api-service'
import { Consultation, Doctor, DoctorCommunicationChannel, Survey } from 'types'

export interface OnboardingState {
  isLoading: boolean
  isEmailAvailable: boolean
  atmHash: string | null
  surveyData: SurveyData | null
  surveySubmission: Record<string, any> | null
  doctors: Doctor[]
  chosenDoctor: Doctor | null
  communicationChannel: DoctorCommunicationChannel | null
  patientAttributes: PatientAttributes | null
  consultation: Consultation | null
  appointmentAt: string | null
  pixQrCode: string | null
  paymentResponse: PaymentResponse | null
  appliedCoupon: { total: number } | null
}

const initialState: OnboardingState = {
  isLoading: false,
  isEmailAvailable: false,
  atmHash: null,
  surveyData: null,
  surveySubmission: null,
  doctors: [],
  chosenDoctor: null,
  communicationChannel: null,
  patientAttributes: null,
  consultation: null,
  appointmentAt: null,
  pixQrCode: null,
  paymentResponse: null,
  appliedCoupon: null
}

export const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    resetOnboardingState: (state) => {
      state = initialState
      return state
    },
    setChosenDoctor: (state, { payload }: PayloadAction<Doctor | null>) => {
      state.chosenDoctor = payload
    },
    setPatientAttributes: (state, { payload }: PayloadAction<PatientAttributes | null>) => {
      state.patientAttributes = payload
    },
    setCommunicationChannel: (state, { payload }: PayloadAction<DoctorCommunicationChannel | null>) => {
      state.communicationChannel = payload
    },
    setSurveySubmission: (state, { payload }: PayloadAction<Record<string, string> | null>) => {
      state.surveySubmission = payload
    },
    setAppointmentAt: (state, { payload }: PayloadAction<string | null>) => {
      state.appointmentAt = payload
    },
    setAtmHash: (state, { payload }: PayloadAction<string | null>) => {
      state.atmHash = payload
    },
    resetConsultation: (state) => {
      state = {
        ...initialState,
        doctors: state.doctors,
        surveyData: state.surveyData
        // LGPD: atmHash is NOT carried over — it belongs to the previous
        // user's WebDiet session and must not leak to the next flow.
      }

      return state
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getSurvey.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getSurvey.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.surveyData = payload
      })
      .addCase(getSurvey.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(getDoctors.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getDoctors.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.doctors = payload
      })
      .addCase(getDoctors.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(checkEmailAvailability.pending, (state) => {
        state.isLoading = true
      })
      .addCase(checkEmailAvailability.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.isEmailAvailable = payload
      })
      .addCase(checkEmailAvailability.rejected, (state) => {
        state.isLoading = false
        state.isEmailAvailable = false
      })
      .addCase(createConsultation.pending, (state) => {
        state.isLoading = true
      })
      .addCase(createConsultation.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.consultation = payload
      })
      .addCase(createConsultation.rejected, (state) => {
        state.isLoading = false
        state.consultation = null
      })
      .addCase(generatePixCode.pending, (state) => {
        state.isLoading = true
      })
      .addCase(generatePixCode.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.pixQrCode = payload.qrcode
      })
      .addCase(generatePixCode.rejected, (state) => {
        state.isLoading = false
        state.pixQrCode = null
      })
      .addCase(consultationCheckout.pending, (state) => {
        state.isLoading = true
      })
      .addCase(consultationCheckout.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.paymentResponse = payload
      })
      .addCase(consultationCheckout.rejected, (state) => {
        state.isLoading = false
        state.paymentResponse = null
      })
      .addCase(consultationApplyCoupon.pending, (state) => {
        state.isLoading = true
      })
      .addCase(consultationApplyCoupon.fulfilled, (state, { payload }) => {
        const { total } = payload
        state.isLoading = false
        state.appliedCoupon = {
          total: parseFloat(total)
        }
      })
      .addCase(consultationApplyCoupon.rejected, (state) => {
        state.isLoading = false
        state.appliedCoupon = null
      })
})

export const {
  resetOnboardingState,
  setChosenDoctor,
  setPatientAttributes,
  setCommunicationChannel,
  setSurveySubmission,
  setAppointmentAt,
  setAtmHash,
  resetConsultation
} = onboardingSlice.actions

export default onboardingSlice.reducer

interface UpdatePatientInfoParams {
  patient_info_token?: string
  order_number: string
  order_id: number
  patient_info: {
    birth_date: string
    prescription_sex: 'M' | 'F'
    rg_number: string
    cpf_number?: string
    zipcode: string
    street_name: string
    street_number: string
    complement: string
    city: string
    state: string
    is_foreign: string
    profession?: string
    salary?: string
  }
}

export const updatePatientInfo = createAsyncThunk(
  'update-patient-info',
  async ({ order_id, ...params }: UpdatePatientInfoParams, { rejectWithValue }) => {
    try {
      const { data } = await railsApiService.apiInstance.post<PaymentResponse>(
        `/api/v1/consultations/${order_id}/update_patient_info`,
        params
      )

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export interface SurveyData {
  id: number
  survey: Survey
  surveyUuid: string
  createdAt: string
  updatedAt: string
  consultationSpecialty: {
    id: number
    slug: string
  }
}
